/**
 * User photos (M72): every user's photo URL, by user id, from one cached call
 * (GET /api/user-avatars). Every avatar in the app reads it through
 * useUserAvatarUrl, so a page with many people costs one request. A user
 * without a photo has no entry; the avatar shows their initials.
 *
 * The URLs are 1h signed Storage URLs that change on every fetch, so this is
 * not the 30s "fresh" tier: a refetch would re-download every photo. Kept 30
 * minutes, dropped from memory before the URLs expire. An admin's set /
 * remove (hooks/useUserAdmin.ts) invalidates it at once.
 */
import { useQuery } from '@tanstack/react-query';
import { httpClient } from '../api/http-client';

export const userAvatarsKey = ['user-avatars'] as const;

const MINUTE = 60 * 1000;

export function useUserAvatars() {
  return useQuery({
    queryKey: userAvatarsKey,
    queryFn: async () => (await httpClient.get<{ avatars: Record<string, string> }>('/user-avatars')).avatars,
    staleTime: 30 * MINUTE,
    gcTime: 50 * MINUTE,
    refetchOnWindowFocus: false,
  });
}

/** The user's photo URL, or null (no photo, no id, or not loaded yet). */
export function useUserAvatarUrl(userId: string | null | undefined): string | null {
  const { data } = useUserAvatars();
  return (userId && data?.[userId]) || null;
}
